/* The search panel. Two things were added on top of Dawn's original
   (Jenny 2026-09-28):

   1. It animates OUT. Dawn only animated the way in; closing dropped the
      `open` attribute and the panel vanished that frame. A <details> has no
      "closing" state of its own, so one is borrowed: .fj-search-closing goes
      on the <details> while it is still open, custom.css runs the fjSearchOut
      keyframes off that class, and only when the animation finishes does
      `open` actually come off.

   2. Closing clears the search. Reopening used to hand back whatever was
      typed last time, plus its results. predictive-search.close(true) empties
      the field and the result list in one call; the plain form is handled by
      hand because it has no such component.

   Every close route goes through close(): the Close button, Escape, and a
   click outside. */

const SEARCH_CLOSING_CLASS = 'fj-search-closing';

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.closeTimer = null;
    this.onAnimationEnd = this.onAnimationEnd.bind(this);

    this.detailsContainer.addEventListener(
      'keyup',
      (event) => event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.summaryToggle.addEventListener(
      'click',
      this.onSummaryClick.bind(this)
    );
    this.querySelector('button[type="button"]').addEventListener(
      'click',
      this.close.bind(this)
    );

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  isClosing() {
    return this.detailsContainer.classList.contains(SEARCH_CLOSING_CLASS);
  }

  onSummaryClick(event) {
    event.preventDefault();

    /* Mid close the panel is still technically open, so a plain open/closed
       check would read "open" and close it a second time. Tapping the icon
       while it is on its way out means "bring it back". */
    if (this.isClosing()) {
      this.cancelClose();
      return;
    }

    event.target.closest('details').hasAttribute('open')
      ? this.close()
      : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.cancelClose();
    this.onBodyClickEvent =
      this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    if (window.fjLockScroll) fjLockScroll('overflow-hidden');
    else document.body.classList.add('overflow-hidden');

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    if (this.isClosing()) return;

    /* Focus and the scroll lock are released straight away. Only the painted
       panel waits for the animation, so the page is usable the whole time. */
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    document.body.removeEventListener('click', this.onBodyClickEvent);
    if (window.fjUnlockScroll) fjUnlockScroll('overflow-hidden');
    else document.body.classList.remove('overflow-hidden');

    this.clearSearch();
    this.animateClose();
  }

  clearSearch() {
    const predictiveSearch = this.querySelector('predictive-search');

    /* close(true) empties the input and drops the results list. */
    if (predictiveSearch && typeof predictiveSearch.close === 'function') {
      predictiveSearch.close(true);
      return;
    }

    const input = this.querySelector('input[type="search"]');
    if (input) input.value = '';
  }

  animateClose() {
    const details = this.detailsContainer;
    const panel = details.querySelector('.search-modal');

    if (!panel) {
      details.removeAttribute('open');
      return;
    }

    details.classList.add(SEARCH_CLOSING_CLASS);
    panel.addEventListener('animationend', this.onAnimationEnd);

    /* animationend is the real signal. The timer only exists so the panel can
       never be left stuck open if the animation is cancelled or never fires,
       which is what happens on a background tab. 600ms clears the 240ms
       close with room to spare. */
    this.closeTimer = setTimeout(() => this.finishClose(), 600);
  }

  onAnimationEnd(event) {
    if (event.target !== event.currentTarget) return;
    this.finishClose();
  }

  finishClose() {
    const details = this.detailsContainer;
    const panel = details.querySelector('.search-modal');

    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    if (panel) panel.removeEventListener('animationend', this.onAnimationEnd);

    details.classList.remove(SEARCH_CLOSING_CLASS);
    details.removeAttribute('open');
  }

  cancelClose() {
    if (!this.isClosing() && !this.closeTimer) return;

    const details = this.detailsContainer;
    const panel = details.querySelector('.search-modal');

    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    if (panel) panel.removeEventListener('animationend', this.onAnimationEnd);

    details.classList.remove(SEARCH_CLOSING_CLASS);
  }
}

customElements.define('details-modal', DetailsModal);
